import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { Portal, type AuthSession } from '@kick/types';
import { loginSchema } from '@kick/validation';
import { api, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { cn } from '@/lib/cn';

export function Login() {
  const iniciar = useAuth((s) => s.iniciar);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [errorCampo, setErrorCampo] = useState<Record<string, string>>({});

  const entrar = useMutation({
    mutationFn: async () => {
      const datos = loginSchema.parse({ username, password, portal: Portal.PANEL });
      const { data } = await api.post<AuthSession>('/auth/login', datos);
      return data;
    },
    onSuccess: (sesion) => iniciar(sesion),
  });

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    setErrorCampo({});

    // Validación en el cliente con el mismo esquema que usa la API.
    const revision = loginSchema.safeParse({ username, password, portal: Portal.PANEL });
    if (!revision.success) {
      const errores: Record<string, string> = {};
      for (const issue of revision.error.issues) {
        const campo = issue.path[0];
        if (typeof campo === 'string' && !errores[campo]) errores[campo] = issue.message;
      }
      setErrorCampo(errores);
      return;
    }

    entrar.mutate();
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      {/* Panel de marca */}
      <div className="hidden flex-col justify-between bg-kick-dark p-12 lg:flex">
        <img src="/logo.png" alt="Kick Domicilios" className="h-16 w-auto self-start" />
        <div>
          <h1 className="text-4xl leading-tight text-white">
            Tu comercio,
            <br />
            <span className="text-kick-red">en movimiento</span>
          </h1>
          <p className="mt-4 max-w-md text-white/60">
            Administra pedidos en vivo, tu menú y tus domiciliarios desde un solo lugar.
          </p>
        </div>
        <p className="text-sm text-white/40">Medellín, Antioquia</p>
      </div>

      {/* Formulario */}
      <div className="flex items-center justify-center bg-kick-light px-6 py-12">
        <div className="w-full max-w-sm">
          <img src="/logo.png" alt="Kick Domicilios" className="mx-auto mb-8 h-14 w-auto lg:hidden" />

          <h2 className="text-2xl text-kick-dark">Ingresar al panel</h2>
          <p className="mt-1 text-sm text-kick-gray">
            Para comercios y administradores de la plataforma.
          </p>

          <form onSubmit={enviar} className="mt-8 space-y-4" noValidate>
            <div>
              <label htmlFor="username" className="mb-1.5 block text-sm font-semibold text-kick-ink">
                Usuario
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="saborpaisa"
                aria-invalid={!!errorCampo.username}
                aria-describedby={errorCampo.username ? 'error-username' : undefined}
                className={cn(
                  'w-full rounded-kick border bg-white px-4 py-3 text-kick-ink outline-none transition',
                  'focus:border-kick-red focus:ring-2 focus:ring-kick-red/20',
                  errorCampo.username ? 'border-red-400' : 'border-kick-gray/30',
                )}
              />
              {errorCampo.username && (
                <p id="error-username" className="mt-1.5 text-sm text-red-600">
                  {errorCampo.username}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-kick-ink">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={verClave ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  aria-invalid={!!errorCampo.password}
                  className={cn(
                    'w-full rounded-kick border bg-white px-4 py-3 pr-12 text-kick-ink outline-none transition',
                    'focus:border-kick-red focus:ring-2 focus:ring-kick-red/20',
                    errorCampo.password ? 'border-red-400' : 'border-kick-gray/30',
                  )}
                />
                <button
                  type="button"
                  onClick={() => setVerClave((v) => !v)}
                  aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-kick-gray transition hover:text-kick-ink"
                >
                  {verClave ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errorCampo.password && (
                <p className="mt-1.5 text-sm text-red-600">{errorCampo.password}</p>
              )}
            </div>

            {entrar.isError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-kick border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{getErrorMessage(entrar.error)}</span>
              </div>
            )}

            <button type="submit" disabled={entrar.isPending} className="btn-kick w-full">
              {entrar.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Ingresando…
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Ingresar
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-kick-gray">
            ¿Olvidaste tu contraseña? Comunícate con el administrador de la plataforma.
          </p>
        </div>
      </div>
    </div>
  );
}
